import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useMediaQuery } from "react-responsive";
import Slider from "react-slick";
import Zoom from "react-medium-image-zoom";
import { ChevronRight, ArrowRight, ChevronLeft } from "lucide-react";
import { shadeTypeDetails } from "../data";
import { optionLinks } from "../../../components/footer/links";
import "react-medium-image-zoom/dist/styles.css";
import { useSelector } from "react-redux";

const ShadeInfoPage = () => {
	const params = useParams();
	const router = useNavigate();
	const shadeType = params.id.includes("fabric=")
		? params.id.split("&")[0].replace(/-/g, "_")
		: params.id.replace(/-/g, "_");
	const fabricType = params.id.includes("fabric=")
		? params.id.split("&")[1].replace("fabric=", "").replaceAll("_", " ")
		: "";
	const shadeData = shadeTypeDetails[shadeType];
	const isMobile = useMediaQuery({ maxWidth: 767 });
	const [activeSection, setActiveSection] = useState("");
	const [activeSubSection, setActiveSubSection] = useState("");
	const country = useSelector((state) => state.country);
	// Set initial active states when component mounts
	useEffect(() => {
		const filteredOptions = optionLinks.filter(
			(option) => shadeData[option.text]?.length
		);
		if (filteredOptions.length > 0) {
			setActiveSection(filteredOptions[0].text);
			if (shadeData[filteredOptions[0].text]?.length > 0) {
				setActiveSubSection(shadeData[filteredOptions[0].text][0].title);
			}
		}
	}, []);
	const [imgGallery, setImgGallery] = useState([]);

	// Spring animation for smoother transitions
	const springTransition = {
		type: "spring",
		stiffness: 400,
		damping: 30,
	};

	// Slider settings
	const sliderSettings = {
		infinite: true,
		centerMode: true,
		slidesToShow: isMobile ? 1 : 3,
		slidesToScroll: 1,
		autoplay: true,
		dots: true,
		arrows: true,
		prevArrow: (
			<button className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow-lg text-primary hover:scale-110 transition-transform">
				<ChevronLeft className="w-6 h-6" />
			</button>
		),
		nextArrow: (
			<button className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow-lg text-primary hover:scale-110 transition-transform">
				<ChevronRight className="w-6 h-6" />
			</button>
		),
		responsive: [
			{
				breakpoint: 1024,
				settings: {
					slidesToShow: 2,
				},
			},
			{
				breakpoint: 768,
				settings: {
					slidesToShow: 1,
					centerMode: false,
				},
			},
		],
	};

	// Load gallery images
	useEffect(() => {
		function loadImage(url, successCallback, errorCallback) {
			const img = new Image();
			img.onload = () => successCallback(url);
			img.onerror = () => errorCallback(url);
			img.src = url;
		}

		async function addImages(shadeType) {
			for (let i = 1; i <= 4; i++) {
				const imageUrl = `/images/shadeinfo/shades/${shadeType}/${i}.jpg`;
				loadImage(
					imageUrl,
					(url) => {
						setImgGallery((prev) => {
							if (prev.some((item) => item.imageUrl === url)) return prev;
							return [...prev, { id: i, imageUrl: url }];
						});
					},
					(url) => console.warn(`Image ${url} failed to load.`)
				);
			}
		}

		if (shadeType) {
			addImages(shadeType.replace(/-/g, "_"));
		}
	}, [shadeType]);

	// Set initial active states when component mounts
	useEffect(() => {
		const filteredOptions = optionLinks.filter(
			(option) => shadeData[option.text]?.length
		);
		if (filteredOptions.length > 0) {
			setActiveSection(filteredOptions[0].text);
			if (shadeData[filteredOptions[0].text]?.length > 0) {
				setActiveSubSection(shadeData[filteredOptions[0].text][0].title);
			}
		}
	}, []);

	return (
		<div className="min-h-screen bg-white overflow-x-hidden">
			<Helmet>
				<title>{shadeData?.metaTitle}</title>
				<meta name="description" content={shadeData?.metaDesc} />
			</Helmet>

			{/* Hero Section */}
			<section className="relative pt-32 pb-20 overflow-hidden">
				<motion.div
					className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl"
					animate={{
						scale: [1, 1.2, 1],
						rotate: [0, 180, 0],
					}}
					transition={{
						duration: 8,
						repeat: Infinity,
						ease: "linear",
					}}
				/>
				<motion.div
					className="absolute bottom-20 right-20 w-72 h-72 bg-secondary/5 rounded-full blur-3xl"
					animate={{
						scale: [1.2, 1, 1.2],
						rotate: [180, 0, 180],
					}}
					transition={{
						duration: 8,
						repeat: Infinity,
						ease: "linear",
					}}
				/>

				<div className="container mx-auto px-4 relative z-10">
					<div className="grid md:grid-cols-2 gap-12 items-center">
						{/* Left Content */}
						<motion.div
							initial={{ opacity: 0, x: -50 }}
							animate={{ opacity: 1, x: 0 }}
							transition={springTransition}
							className="space-y-8"
						>
							{/* <motion.div
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium"
							>
								<Star className="w-4 h-4 mr-2" />
								Premium Collection
							</motion.div> */}

							<h1 className="text-5xl md:text-7xl font-bold leading-tight">
								{shadeData?.hero.header}
								<motion.div
									className="absolute -inset-2 -skew-y-3 bg-primary/10 -z-10 rounded-2xl"
									initial={{ scale: 0 }}
									animate={{ scale: 1 }}
									transition={{ delay: 0.3 }}
								/>
							</h1>

							<p className="text-xl text-gray-600">{shadeData?.hero.body}</p>

							<div className="flex flex-wrap gap-4">
								{shadeData?.actions?.map((action, index) => (
									<motion.button
										key={index}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.95 }}
										className={`px-8 py-4 rounded-xl font-medium flex items-center gap-2 group ${
											index === 0
												? "bg-primary text-white shadow-lg shadow-primary/30"
												: "border-2 border-gray-200 hover:border-primary/30"
										}`}
										onClick={() => {
											if (action.text === "Start Customizing") {
												router(
													`/customize/details?default=${shadeType}&fabric=${fabricType}`
												);
											}
										}}
									>
										{action.text}
										<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
									</motion.button>
								))}
							</div>
						</motion.div>

						{/* Right Image */}
						<motion.div
							initial={{ opacity: 0, x: 50 }}
							animate={{ opacity: 1, x: 0 }}
							transition={springTransition}
							className="relative"
						>
							<img
								src={shadeData?.hero.img}
								alt={shadeData?.hero.header}
								className="w-full h-full object-cover rounded-3xl shadow-2xl"
							/>

							{shadeData?.hero.price && (
								<motion.div
									initial={{ opacity: 0, scale: 0 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ delay: 0.5 }}
									className=" absolute top-6 right-6 bg-white/90 backdrop-blur-sm p-6 rounded-xl"
								>
									<div className="text-sm tracking-wider font-light">
										Starting from
									</div>
									<div className="mt-1 text-3xl tracking-wider font-semibold text-primary">
										$
										{country === "USA"
											? `${(shadeData.hero.price * 0.75).toFixed(2)} USD`
											: `${shadeData.hero.price} CAD`}
									</div>
								</motion.div>
							)}
						</motion.div>
					</div>
				</div>
			</section>

			<section className="py-24 relative bg-white">
				{/* Top Navigation */}
				<div className="container mx-auto px-4 mb-16">
					<div className="flex flex-col items-center">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							className="flex flex-wrap justify-center gap-4 mb-8"
						>
							{optionLinks
								.filter((option) => shadeData[option.text]?.length)
								.map((option, index) => (
									<motion.button
										key={option.text}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.95 }}
										onClick={() => {
											setActiveSection(option.text);
											// Set first subsection of new section as active
											if (shadeData[option.text]?.length > 0) {
												setActiveSubSection(shadeData[option.text][0].title);
											}
										}}
										className={`px-6 py-3 rounded-xl font-medium text-lg transition-all
                    ${
											activeSection === option.text
												? "bg-primary text-white shadow-lg shadow-primary/20"
												: "bg-gray-50 text-gray-700 hover:bg-primary/10"
										}`}
									>
										{option.text}
									</motion.button>
								))}
						</motion.div>
					</div>
				</div>

				{/* Content Section */}
				<div className="container mx-auto px-4">
					<AnimatePresence mode="wait">
						{activeSection && (
							<motion.div
								key={activeSection}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								className="grid grid-cols-[2fr,3fr] gap-12"
							>
								{/* Left Side - Navigation */}
								<div className="space-y-6 sticky top-24 self-start">
									<h2 className="text-4xl font-bold">{activeSection}</h2>
									<div className="space-y-2">
										{shadeData[activeSection]?.map((data, idx) => (
											<motion.button
												key={idx}
												whileHover={{ x: 4 }}
												onClick={() => setActiveSubSection(data.title)}
												className={`block w-full text-left p-4 rounded-xl transition-all
                        ${
													activeSubSection === data.title
														? "bg-primary/10 text-primary font-medium"
														: "hover:bg-gray-50 text-gray-600"
												}`}
											>
												{data.title}
											</motion.button>
										))}
									</div>
								</div>

								{/* Right Side - Content */}
								<div>
									<AnimatePresence mode="wait">
										{activeSubSection && (
											<motion.div
												key={activeSubSection}
												initial={{ opacity: 0, y: 20 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -20 }}
												transition={{ duration: 0.3 }}
												className="relative"
											>
												{shadeData[activeSection]
													?.filter((data) => data.title === activeSubSection)
													.map((data, index) => (
														<div key={index} className="group">
															{/* Media */}
															<div className=" rounded-3xl overflow-hidden mb-8">
																{data.media[0].endsWith(".mp4") ? (
																	<Zoom>
																		<video
																			autoPlay
																			loop
																			muted
																			playsInline
																			className="w-full h-full aspect-[16/9]"
																		>
																			<source
																				src={data.media[0]}
																				type="video/mp4"
																			/>
																		</video>
																	</Zoom>
																) : (
																	<Zoom>
																		<img
																			src={data.media[0]}
																			alt={data.title}
																			className="w-auto md:h-[600px] "
																		/>
																	</Zoom>
																)}
															</div>

															{/* Content */}
															<div className="max-w-2xl">
																<h3 className="text-2xl font-bold mb-4">
																	{data.title}
																</h3>
																<div className="prose prose-lg text-gray-600">
																	{Array.isArray(data.body) ? (
																		data.body.map((text, idx) => (
																			<div key={idx} className="mb-4 last:mb-0">
																				{text}
																			</div>
																		))
																	) : (
																		<div>{data.body}</div>
																	)}
																</div>
															</div>

															{/* Visual indicator */}
															<motion.div
																className="absolute -left-4 top-0 bottom-0 w-1 bg-primary/20 rounded-full"
																initial={{ scaleY: 0, opacity: 0 }}
																animate={{ scaleY: 1, opacity: 1 }}
																transition={{ duration: 0.5 }}
															/>
														</div>
													))}
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>

				{/* Background decoration */}
				<div className="absolute inset-0 pointer-events-none">
					<div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
					<div className="absolute bottom-0 right-1/4 w-64 h-64 bg-secondary/5 rounded-full blur-3xl" />
				</div>
			</section>

			{/* Gallery Section */}
			{imgGallery.length > 0 && shadeType !== "cellular" && (
				<section className="py-24 bg-black relative overflow-hidden">
					<div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary skew-y-2 transform origin-top-right" />
					<div className="relative container mx-auto px-4">
						<motion.h2
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							className="text-4xl md:text-6xl font-bold text-white text-center mb-16"
						>
							Get Inspired
						</motion.h2>

						<Slider {...sliderSettings} className="inspirationSlider">
							{imgGallery.map((item) => (
								<div key={item.id} className="px-4">
									<motion.div
										whileHover={{ y: -10 }}
										className="bg-white p-4 rounded-2xl"
									>
										<Zoom>
											<img
												src={item.imageUrl}
												alt="Inspiration"
												className="w-full aspect-[4/3] object-cover rounded-xl"
											/>
										</Zoom>
									</motion.div>
								</div>
							))}
						</Slider>
					</div>
				</section>
			)}
		</div>
	);
};

export default ShadeInfoPage;
